/**
 * Helper function for converting to a valid key
 * @param {Object} step - The step object containing the title
 * @param {String} itemLabel - The label of the form item
 * @param {Number} [index] - Optional index of the item in the array, used as fallback if label is missing
 * @returns {String} The serial key in the format encodedStepTitle-encodedLabel or encodedStepTitle-index
 */
export const createSerialKey = (step, itemLabel, index) => {
	// Log parameters for debugging
	//console.debug("Creating serial key with:", { step, itemLabel, index });
	
	// Use original identifiers without transformation
	if (!step || (!itemLabel && index === undefined)) return "";
	
	// Make sure we use only the pure label text or fallback to index
	let cleanLabel;
	if (itemLabel) {
		// Remove "0" at the end, if present
		cleanLabel = String(itemLabel).replace(/\s*0$/, '');
	} else {
		// Fallback to index if label is missing
		cleanLabel = `item-${index}`;
	}
	
	// Encode each part to allow safe decoding and avoid special characters issues
	const encodedStepTitle = encodeURIComponent(step.title || (step.id ?? ""));
	const encodedLabel = encodeURIComponent(cleanLabel);
	
	// Create the serial key
	const serialKey = `${encodedStepTitle}/${encodedLabel}/${encodeURIComponent(`${step.id ?? "undefined"}`)}/${encodeURIComponent(`${index ?? "undefined"}`)}`;
	
	// Log the generated serial key
	//console.debug("Generated serial key:", serialKey);
	
	// Return formatted key with encoded parts
	return serialKey;
};

/**
 * Cleans a value for serialization
 */
export const cleanValue = (value) => {
	if (typeof value === 'string') {
		// Remove "0" at the end, if present
		return value.replace(/\s*0$/, '');
	}
	return value;
};

/**
 * Translation table for field types based on type IDs
 */
export const FIELD_TYPES = {
	1: 'text',      // Text field
	2: 'email',     // Email
	3: 'url',       // URL
	4: 'tel',       // Phone
	5: 'date',      // Date
	6: 'time',      // Time
	7: 'datetime',  // Date & Time
	8: 'textarea',  // Text area
	9: 'checkbox',  // Checkbox
	10: 'radio',    // Radio Button
	11: 'select',   // Select Dropdown
	12: 'number',   // Numeric Field
	13: 'file',     // File upload Field
	99: 'hidden',   // Hidden Field
};

/**
 * Translation table for form representations
 */
export const FORM_REPRESENTATIONS = {
	1: 'tiles',     // Tile representation
	2: 'list',      // List view
	3: 'form',      // Standard form
};

/**
 * Get field type based on type ID with fallback
 * @param {Number|String} typeId - The type ID or string fallback
 * @returns {String} The field type string
 */
export const getFieldType = (typeId, fallback = 'text') => {
	if (typeof typeId === 'string') {
		return typeId; // Return string directly if it's already a string
	}
	return FIELD_TYPES[typeId] || fallback;
};

/**
 * Helper function to generate unique IDs
 */
export const generateUniqueId = (prefix = 'item') => {
	return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Helper function to render a single form element in SimpleForm
 */
export const renderFormItem = (item, idx) => {
	if (item.type === 9 || item.type === 10) {
		// Generate unique ID for checkbox/radio to avoid conflicts
		const inputId = `simple-${item.type}-${idx}`;
		
		return (
			<div
				key={idx}
				className="mb-4">
				<div className="form-check">
					<input
						id={inputId}
						className="form-check-input"
						type={item.type === 9 ? "checkbox" : "radio"}
						required={!!item.required}
						name={item.type === 10 ? `simple-radio-group-${idx}` : `simple-${idx}`} 
						value={item.value || item.label} 
					/>
					<label
						className="form-check-label"
						htmlFor={inputId}>
						{item.label}
					</label>
				</div>
			</div>
		);
	}
	
	// Import the Input component, as we use it in renderFormItem
	const Input = require("../../Input/Input").default;
	
	if (item.type === 11) {
		return (
			<div
				key={idx}
				className="mb-4">
				<label htmlFor={`simple-${idx}`}>{item.label}</label>
				<select
					id={`simple-${idx}`}
					className="form-select"
					required={!!item.required}>
					<option value="" disabled selected>{item.placeholder || "Please select"}</option>
					{item.options &&
						item.options.map((opt, oIdx) => (
							<option
								key={oIdx}
								value={opt.value}>
								{opt.label}
							</option>
						))}
				</select>
			</div>
		);
	}
	if (item.type === 8) {
		return (
			<div
				key={idx}
				className="mb-4">
				<textarea
					id={`simple-${idx}`}
					placeholder={item.placeholder || " "}
					required={!!item.required}
					className="form-control"
					rows="3"
				/>
				<label htmlFor={`simple-${idx}`}>{item.label}</label>
			</div>
		);
	}
	return (
		<div
			key={idx}
			className="mb-4">
			<Input
				type={getFieldType(item.type)}
				label={item.label}
				placeholder={item.placeholder || ""}
				required={!!item.required}
				name={`simple-${idx}`}
			/>
		</div>
	);
};

/**
 * Separates data-* attributes from other props
 * @param {Object} props - Object containing all props
 * @returns {Object} Object with { dataAttributes, otherProps }
 */
export const separateDataAttributes = (props) => {
	const dataAttributes = {};
	const otherProps = {};
	
	Object.entries(props || {}).forEach(([key, value]) => {
		if (key.startsWith('data-')) {
			dataAttributes[key] = value;
		} else {
			otherProps[key] = value;
		}
	});
	
	return { dataAttributes, otherProps };
};
